// s101 map review (Dr. Rami, Build 67: "opens very zoomed out", "every time I zoom in it auto zooms
// out", "scrolling around the map is clunky"). The two camera decisions of DoctorsMap, pure, so the
// gate check-map-camera runs them.

const R = 3958.8
export const milesBetweenPts = (a, b) => {
  const rad = (x) => (x * Math.PI) / 180
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** The half-width (miles) to frame around the patient: the closest 8 pins inside the search distance,
 *  always reaching the nearest Orella doctor; never under 2 mi, never over the distance; 10 mi
 *  (or the distance) with no pin inside it. */
export function nearestViewMiles(center, doctors, radiusMiles, orellaOf) {
  const near = (doctors || [])
    .filter((d) => d && d.practice_lat != null && d.practice_lng != null)
    .map((d) => ({ d, mi: milesBetweenPts(center, { lat: Number(d.practice_lat), lng: Number(d.practice_lng) }) }))
    .filter((x) => Number.isFinite(x.mi) && x.mi <= radiusMiles)
    .sort((x, y) => x.mi - y.mi)
  if (!near.length) return Math.min(radiusMiles, 10)
  let reach = near[Math.min(near.length, 8) - 1].mi
  const firstOrella = typeof orellaOf === 'function' ? near.find((x) => orellaOf(x.d)) : null
  if (firstOrella) reach = Math.max(reach, firstOrella.mi)
  return Math.min(radiusMiles, Math.max(2, reach * 1.15))
}

/** The search a camera position belongs to. With a search distance the doctor array is NOT part of
 *  it (a screen builds a new array every render); without one (the Doctors screens frame their
 *  pins) the set of doctor ids is. */
export function cameraViewKey({ center, centerPrecise, fitRadiusMiles, doctors }) {
  const r3 = (v) => (typeof v === 'number' ? v.toFixed(3) : '-')
  const radius = Number.isFinite(fitRadiusMiles) && fitRadiusMiles > 0
  const idSet = radius ? '' : (doctors || []).map((d) => String(d && d.id)).sort().join(',')
  return `${r3(center && center.lat)},${r3(center && center.lng)}|${radius ? fitRadiusMiles : '-'}|${centerPrecise ? 1 : 0}|${idSet}`
}

/** The centre part of a view key (the first field of cameraViewKey). */
const centrePart = (key) => (typeof key === 'string' ? key.split('|')[0] : null)
/** The distance part of a view key: the radius (second field) and DoctorsMap's '|x' suffix (fitExact,
 *  the fifth field). The precise flag (third field) is NOT part of it. */
const distancePart = (key) => {
  if (typeof key !== 'string') return null
  const f = key.split('|')
  return `${f[1]}|${f[4] || ''}`
}
const samePoint = (a, b) => !!(a && b && Math.abs(a.lat - b.lat) < 0.002 && Math.abs(a.lng - b.lng) < 0.002)

/** PROD-1420: the idle handler's record of a view the patient settled on. `reported` keeps the last
 *  few since the camera's own fit, so an area answer that lands after a newer move is still known. */
export function cameraReported(state, ctr) {
  return { ...state, lastReported: ctr, reported: [...((state && state.reported) || []).slice(-7), ctr] }
}

/** What the camera does on a render: 'fit', 'hold' (a new search the patient already framed, i.e.
 *  "Search this area"), or 'none'. state = { key, fittedWithPins, userTookOver, lastReported, reported }.
 *  ★ PROD-1420 (Alex 2026-11-03: "the map sometimes resets when I search on it"): an area answer for an
 *  OLDER view the patient reported (they moved again while it was in flight) holds too, instead of
 *  snapping back to it; and a new DISTANCE at the same centre is a new search and fits, also after the
 *  patient moved the map. Only the distance forces that fit: the first area search at the patient's
 *  own point changes just the precise flag (device point -> area), and that holds (R1420 H1, the s101
 *  zoom-in-place case). */
export function cameraStep(state, { viewKey, hasPins, center }) {
  if (viewKey !== state.key) {
    const newDistance = state.key != null && centrePart(state.key) === centrePart(viewKey) && distancePart(state.key) !== distancePart(viewKey)
    const held = !newDistance && (samePoint(state.lastReported, center)
      || (!!state.userTookOver && (state.reported || []).some((p) => samePoint(p, center))))
    return { action: held ? 'hold' : 'fit', state: { ...state, key: viewKey, userTookOver: held, fittedWithPins: held || hasPins, reported: held ? (state.reported || []) : [] } }
  }
  if (hasPins && !state.fittedWithPins && !state.userTookOver) {
    return { action: 'fit', state: { ...state, fittedWithPins: true } }
  }
  return { action: 'none', state }
}
